import { stairOpeningBounds } from "./stair_opening"
import { hatchBlocksPassage } from "game-common/room/hatch"
import { Coordinate, TileSize } from "game-common/models"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { stairTileAt } from "game-common/room/ember/stairs"
import { LadderTop, ladderTopAt } from "game-common/room/ember/ladder_top"
import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"

// The upright ladder's feet stand 4px above the bottom of their cell.
const FOOT_GROUND_OFFSET = TileSize / 2 - 4
/** The ladder's splayed feet spread 41px. Anything smaller misses
 * a player pressed against a wall beside the stair (a 5x5 house stops them 15.5px from its middle). */
export const FOOT_RADIUS = 20.5
/** The player's bounding circle: round the body's 25px collision box, centred on their soles. */
export const PLAYER_RADIUS = 12.5 * Math.SQRT2
/** The part of the player's footprint that has to be over the foot before they climb. */
const CLIMB_OVERLAP = 0.5
/** One frame never walks further than this. A longer jump is a teleport or a correction. */
const MAX_STEP = 48
/** Stand at the ladder's foot until the server starts the climb, or give up after this. */
const HOLD_MS = 750
/** Entry is 18px south of the former one-pixel inset from the north lip. */
const DESCENT_ENTRY_INSET = 19
/** From a roof, the top of a ladder against it is taken this close to the roof's edge, before the soles leave it. */
const ROOF_EDGE_REACH = 6
/** ...and within this far of the ladder's own row or column, along that edge. */
const ROOF_EDGE_SPAN = 16

/** How much of the smaller of two circles, centres `d` apart, lies inside the other. */
export const circleOverlap = (d: number, a: number, b: number): number => {
    if (d >= a + b) return 0
    if (d <= Math.abs(a - b)) return 1
    const lens = a * a * Math.acos((d * d + a * a - b * b) / (2 * d * a)) +
        b * b * Math.acos((d * d + b * b - a * a) / (2 * d * b)) -
        Math.sqrt((-d + a + b) * (d + a - b) * (d - a + b) * (d + a + b)) / 2
    return lens / (Math.PI * Math.min(a, b) ** 2)
}

/** The distance between the soles and the foot at which the overlap is exactly CLIMB_OVERLAP. */
export const CLIMB_DISTANCE = (() => {
    let near = Math.abs(FOOT_RADIUS - PLAYER_RADIUS), far = FOOT_RADIUS + PLAYER_RADIUS
    for (let i = 0; i < 50; i++) {
        const mid = (near + far) / 2
        if (circleOverlap(mid, FOOT_RADIUS, PLAYER_RADIUS) >= CLIMB_OVERLAP) near = mid
        else far = mid
    }
    return near
})()

export interface LadderClimb {
    roomId: string
    coordinate: Coordinate
    interaction: "ascend" | "descend"
    /** A bare vertical shaft is entered in place, in either direction. */
    shaft?: boolean
    /** The top of a ladder against a roof, taken from the roof: hold there rather than walk off its edge. */
    beside?: boolean
}

/**
 * Whether soles on the roof beside the top of a ladder against it (cell `at`) are at the roof's edge, toward the ladder,
 * and the player presses on over it (openspec movement/ladders). `top.landing` is where a body stands on that roof.
 */
const takesBesideTop = (top: Extract<LadderTop, { kind: "beside" }>, at: Coordinate, soles: Coordinate,
    intent: Coordinate): { touching: boolean; pressing: boolean } => {
    const feet = { x: top.landing.x, y: top.landing.y + BODY_GROUND_OFFSET }
    if (top.side === "north") {
        // The roof's front edge is half a cell south of where a body stands in its middle.
        const edge = feet.y + TileSize / 2
        return { touching: soles.y >= edge - ROOF_EDGE_REACH && soles.y <= edge + ROOF_EDGE_REACH &&
            Math.abs(soles.x - (at.x + .5) * TileSize) <= TileSize / 2 - 4,
            pressing: intent.y > 0 && intent.y >= Math.abs(intent.x) }
    }
    const toward = top.side === "east" ? -1 : 1
    const edge = top.side === "east" ? (at.x + 1) * TileSize : at.x * TileSize
    return { touching: (soles.x - edge) * -toward <= ROOF_EDGE_REACH && (soles.x - edge) * -toward >= -ROOF_EDGE_REACH &&
        Math.abs(soles.y - feet.y) <= ROOF_EDGE_SPAN,
        pressing: intent.x * toward > 0 && intent.x * toward >= Math.abs(intent.y) }
}

/** A stair the player may take, with the verb its tile offers for it. */
const usableStairAt = (provider: TileMapMetaProvider, at: Coordinate, direction?: "up" | "down") => {
    const stair = stairTileAt(provider, at, direction) ?? stairTileAt(provider, at)
    if (!stair || hatchBlocksPassage(provider, at)) return undefined
    const delta = provider.getMapDeltaAt(at.x, at.y, "sprite2")
    const interactions = !delta?.removed && delta?.context?.tileInteractions
    const name = stair.direction === "up" ? "ascend" : "descend"
    const command = interactions && interactions.find((action: { name: string }) => action.name === name)
    // Include the top/bottom endpoints: they share the shaft's grip even with one exit.
    const continuous = !!(delta?.context?.context?.stairLandings ?? delta?.context?.stairLandings)
    return command ? { stair, command, continuous, interaction: name as LadderClimb["interaction"] } : undefined
}

/**
 * Takes an upright stair while half the player's footprint is over its foot and they press up.
 * Downstairs starts nineteen pixels inside the opening
 * beyond its north lip, measured at the player's soles, while pressing down.
 * "Pressing up" means heading more up the screen
 * than sideways, so up with a keyboard diagonal counts and side to side never does. Arriving on a foot
 * -- off the stair itself, or a teleport -- and having just asked to take it do not count: that foot
 * goes again only once the player has stepped off it. Bare stacked shafts instead take
 * either direction directly from their shared cell, including immediately after a handoff.
 */
export class LadderApproach {
    private roomId?: string
    private previous?: Coordinate
    // Stairs ("x,y" in roomId) the player stands on but may not climb yet.
    private blocked = new Set<string>()
    private pending?: LadderClimb & { until: number }

    /** Forget the walk so far: the next frame starts a new one. */
    reset = () => {
        this.previous = undefined
        this.blocked.clear()
    }

    /** A climb was asked for and the server has not started it yet. */
    holding = (roomId: string, now: number, interaction?: LadderClimb["interaction"]): boolean => {
        if (this.pending && (this.pending.roomId !== roomId || now >= this.pending.until)) this.pending = undefined
        return !!this.pending && (!interaction || this.pending.interaction === interaction)
    }

    /** Keep hold of a shaft while its descent request is in flight, rather than stepping into air. */
    holdingPosition = (roomId: string, now: number): boolean =>
        this.holding(roomId, now) && (this.pending.interaction === "ascend" || !!this.pending.shaft || !!this.pending.beside)

    /** The server started a climb (or the player otherwise left the ground): nothing to wait for. */
    release = () => {
        this.pending = undefined
    }

    /** Feed one frame of movement; returns the climb to ask for, at most once per approach. */
    update = (provider: TileMapMetaProvider, roomId: string, location: Coordinate, intent: Coordinate,
        now: number): LadderClimb | undefined => {
        const sameRoom = roomId === this.roomId
        // Arriving somewhere (a room, a teleport) is not walking there: a foot it lands on is not one
        // it pressed up on.
        const fresh = !this.previous || !sameRoom ||
            Math.hypot(location.x - this.previous.x, location.y - this.previous.y) > MAX_STEP
        this.roomId = roomId
        this.previous = { x: location.x, y: location.y }
        if (!sameRoom) this.pending = undefined
        if (this.holding(roomId, now)) return undefined
        const vertical = intent.y !== 0 && Math.abs(intent.x) <= Math.abs(intent.y)
        const northward = vertical && intent.y < 0
        const soles = { x: location.x, y: location.y + BODY_GROUND_OFFSET }
        const reach = CLIMB_DISTANCE
        const blocked = new Set<string>()
        let climb: { climb: LadderClimb; distance: number } | undefined
        const x0 = Math.floor((soles.x - reach) / TileSize), x1 = Math.floor((soles.x + reach) / TileSize)
        const y0 = Math.floor((soles.y - FOOT_GROUND_OFFSET - reach) / TileSize)
        const y1 = Math.floor((soles.y - FOOT_GROUND_OFFSET + reach) / TileSize)
        for (let x = x0; x <= x1; x++) {
            for (let y = y0; y <= y1; y++) {
                const key = `${x},${y}`
                // The top of a ladder against a roof, reached from the roof beside it.
                const top = stairTileAt(provider, { x, y }, "down") && ladderTopAt(provider.getFloorSupportProvider(), provider, { x, y })
                if (top && top.kind === "beside") {
                    const descent = usableStairAt(provider, { x, y }, "down")
                    if (descent?.interaction !== "descend") continue
                    const { touching, pressing } = takesBesideTop(top, { x, y }, soles, intent)
                    if (!touching) continue
                    const distance = Math.hypot(soles.x - top.landing.x, soles.y - top.landing.y - BODY_GROUND_OFFSET)
                    if (fresh || this.blocked.has(key)) blocked.add(key)
                    else if (pressing && (!climb || distance < climb.distance))
                        climb = { climb: { roomId, coordinate: { x, y }, interaction: "descend", beside: true }, distance }
                    continue
                }
                const usable = usableStairAt(provider, { x, y }, vertical ? northward ? "up" : "down" : undefined)
                if (!usable) continue
                const pressing = usable.stair.direction === "up" ? northward : vertical && intent.y > 0
                const opening = usable.stair.direction === "down" && !usable.continuous ? stairOpeningBounds({ x, y }) : undefined
                const distance = usable.continuous ? Math.hypot(location.x - (x + .5) * TileSize, location.y - (y + .5) * TileSize)
                    : opening ? Math.abs(soles.x - (x + .5) * TileSize)
                    : Math.hypot(soles.x - (x + .5) * TileSize, soles.y - ((y + .5) * TileSize + FOOT_GROUND_OFFSET))
                const touching = usable.continuous ? Math.floor(location.x / TileSize) === x && Math.floor(location.y / TileSize) === y : opening
                    ? soles.x >= opening.left && soles.x <= opening.right &&
                        soles.y >= opening.north + DESCENT_ENTRY_INSET && soles.y < opening.south
                    : distance <= CLIMB_DISTANCE
                if (!touching) continue
                if (!usable.continuous && (fresh || this.blocked.has(key))) blocked.add(key)
                else if (pressing && (!climb || distance < climb.distance))
                    climb = { climb: { roomId, coordinate: { x, y }, interaction: usable.interaction,
                        ...(usable.continuous ? { shaft: true } : {}) }, distance }
            }
        }
        this.blocked = blocked
        if (!climb) return undefined
        blocked.add(`${climb.climb.coordinate.x},${climb.climb.coordinate.y}`)
        this.pending = { ...climb.climb, until: now + HOLD_MS }
        return climb.climb
    }
}
